import { StringUtils } from '@/common/utils/stringUtils';
import { validateFormat } from '@/common/validator/validatorHelpers';
import { helpers, integer, minValue, required } from '@vuelidate/validators';
import { GameMatchCostState } from '@/modules/game/match/model/mixins';

export const updateValidator = {
    name: { required },
    winLoseGroup: { required },
    allowInvitation: { required },
    levelTimeout: { required, integer, minValue: minValue(1) },
    findingMatchTimeout: {
        required: helpers.withMessage('Value is required', (val, ctx) => ctx.type !== 1 || !!val),
        integer,
        minValue: minValue(1)
    },
    costs: {
        required,
        format: validateFormat(/^\d+(;\d+)*$/),
        value: helpers.withMessage('All costs must be >= 0', (value: string) =>
            value.split(StringUtils.Characters.Semicolon).every(v => Number(v) >= 0)
        )
    },
    costStates: {
        required,
        format: validateFormat(/^\d+(;\d+)*$/),
        matchesCostCount: helpers.withMessage(
            "Length doesn't match costs",
            (v: string, siblings) =>
                v.split(GameMatchCostState.SEPARATOR).filter(s => s.trim() !== '').length ===
                siblings.costs.split(GameMatchCostState.SEPARATOR).filter((s: string) => s.trim() !== '').length
        ),
        validStates: helpers.withMessage(
            `Invalid state. Valid values: ${Object.values(GameMatchCostState.STATES)
                .map(s => s.value + '(' + s.label + ')')
                .join(' ')}`,
            (value: string) => {
                const valid = Object.values(GameMatchCostState.STATES).map(s => s.value);
                return value.split(GameMatchCostState.SEPARATOR).every(s => valid.includes(Number(s.trim())));
            }
        )
    },
    potRatePercents: {
        required,
        format: validateFormat(/^\d+(\.\d+)?(,\d+(\.\d+)?)*(;\d+(\.\d+)?(,\d+(\.\d+)?)*)*$/),
        value: helpers.withMessage('All pot percents must be <= 100', (value: string) =>
            value
                .split(StringUtils.Characters.Semicolon)
                .flatMap(p => p.split(StringUtils.Characters.Comma))
                .every(v => Number(v) <= 100)
        ),
        size: helpers.withMessage(
            "Length doesn't match costs",
            (value: string, ctx: any) =>
                value.split(StringUtils.Characters.Semicolon).length ===
                ctx.costs.split(StringUtils.Characters.Semicolon).length
        )
    },
    potRateWeights: {
        required,
        format: validateFormat(/^\d+(,\d+)*(;\d+(,\d+)*)*$/),
        value: helpers.withMessage('All pot weights must be > 0', (value: string) =>
            value
                .split(StringUtils.Characters.Semicolon)
                .flatMap(w => w.split(StringUtils.Characters.Comma))
                .every(v => Number(v) > 0)
        ),
        size: helpers.withMessage("Length doesn't match pot rate percents", (value: string, ctx: any) => {
            const splitGroups = (val: string) =>
                val.split(StringUtils.Characters.Semicolon).map(g => g.split(StringUtils.Characters.Comma));
            const percentGroups = splitGroups(ctx.potRatePercents);
            const weightGroups = splitGroups(value);

            return (
                percentGroups.length === weightGroups.length &&
                percentGroups.every((group, i) => group.length === weightGroups[i].length)
            );
        })
    }
};

export const creationValidator = {
    ...updateValidator,
    variantId: { required },
    type: { required },
    maxRound: { required, integer, minValue: minValue(1) },
    maxPoint: { required, integer, minValue: minValue(1) },
    tableCreditRatio: { required, format: validateFormat(/^\d+(;\d+)*$/) }
};
